import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Star, Lock, Check, ArrowLeft, RotateCw } from 'lucide-react';
import { Piko } from '../components/Piko';
import { audio } from '../core/audio';
import { getState, hasTriedGame } from '../core/storage';
import { t } from '../core/i18n';
import { WORLDS, PLAYABLE_REGIONS, currentJourney, getGame, gameName, gameLockState, starsNeeded, gameBg } from '../content/games';
import { useEntitlement } from '../core/entitlement';

// ─────────────────────────────────────────────────────────────────────────────
// ONE scroll container, ALL EIGHT WORLDS, in PLAYABLE_REGIONS order.
//
// Why: each world now holds only 3–4 games, so a single world's path was 914px
// against a 900px viewport — 14px of travel, which reads as "the map cannot
// scroll". It was never a CSS bug; there was simply nothing to scroll. Putting
// the whole journey in one container gives the parent ~6,000px to explore and,
// deliberately, lets them scroll into the six Pro worlds they have not bought.
//
// THE SCROLL IS NOT FOR THE CHILD. A 2-year-old cannot deliberately scroll
// thousands of pixels and must never need to in order to play. On open this
// screen jumps straight to the child's current node so "Main!" is on screen
// immediately. Everything below is a parent-facing discovery affordance reached
// only by deliberate scrolling. Vertical scroll only — `touch-action: pan-y`
// keeps swipe, drag, pinch and horizontal pan off this screen entirely.
// ─────────────────────────────────────────────────────────────────────────────

const ROW_H = 168;
const BANNER_H = 104;          // per-world banner at the top of each section
const PATH_TAIL = 90;          // room under the last node for its stars + pill
const HEADER_PAD = 128;        // clearance for the fixed header
// Where the current node sits on screen after the landing scroll. It must clear
// the header by more than the 148px Piko stands above his node, or the child's
// own marker lands behind the header and is simply not there. 140 put him at
// y=-8. This is the same on-screen position the single-world screen produced.
const LAND_OFFSET = 280;
const SCROLL_KEY = 'pp_worldpath_scroll';
const NEAR_MARGIN = '900px 0px'; // how early a section is worth its images

const WORLD_COLORS = { garden: '#3F7C3D', forest: '#33637A', island: '#1F7C9E', shapes: '#B07A2A', size: '#2D8FA0', animals: '#7A5FA8', move: '#C05F5F', daily: '#5F8AC0' };

// Section heights are FIXED and known from the game count alone. That is what
// makes it safe to render a far-away section as a bare placeholder: the page is
// exactly as tall either way, so scrollHeight never moves and the page never
// jumps under the parent's finger.
const pathHeight = (r) => r.games.length * ROW_H + PATH_TAIL;
const sectionHeight = (r) => BANNER_H + pathHeight(r);
const regionIndex = (id) => PLAYABLE_REGIONS.findIndex((r) => r.id === id);
const colourOf = (id) => WORLD_COLORS[id] || '#3F7C3D';

function PathNode({ game, i, lock, completed, bestStars, isCurrent, need, onTap, en, worldColor }) {
  const left = i % 2 === 0 ? '27%' : '73%';
  const pro = lock === 'pro';
  const locked = lock !== 'open';
  const art = !pro && lock !== 'stars';
  const border = completed ? `6px solid ${worldColor}`
    : isCurrent ? '6px solid #FFD166'
    : locked ? '4px dashed rgba(255,255,255,0.9)'
    : '6px solid rgba(255,255,255,0.95)';
  return (
    <div className="absolute -translate-x-1/2 flex flex-col items-center" style={{ left, top: i * ROW_H + 30 }}>
      <motion.button data-testid={`level-node-${game.id}`} onClick={onTap} whileTap={{ scale: 0.9 }}
        animate={isCurrent ? { scale: [1, 1.08, 1] } : {}}
        transition={{ repeat: Infinity, duration: 1.6 }}
        className="relative w-[92px] h-[92px] rounded-full flex items-center justify-center shadow-2xl"
        style={{
          background: pro ? 'rgba(29,53,87,0.85)' : lock === 'stars' ? 'rgba(255,255,255,0.8)' : '#fff',
          border,
        }}>
        {/* An <img>, not a CSS background. 31 thumbnails authored at 896×1200
            decode to ~4.3 MB each; as background-image the browser must decode
            every one at natural size and cannot defer offscreen ones. As an
            <img loading="lazy"> it downscales to 92px and skips what is not
            near the viewport — the difference between usable and unusable on a
            low-end Android tablet.
            gameBg() stays the ONE source of the path (rebuilding it by hand
            here once made the map and the game stage disagree); the world
            background survives as the same safety net it was, now via onError. */}
        {art && (
          <img src={gameBg(game)} alt="" aria-hidden="true" loading="lazy" decoding="async" width={92} height={92}
            onError={(e) => {
              const fb = WORLDS[game.world]?.bg;
              if (fb && !e.currentTarget.src.endsWith(fb)) e.currentTarget.src = fb;
              else e.currentTarget.style.visibility = 'hidden';
            }}
            className="absolute inset-0 w-full h-full object-cover rounded-full" />
        )}
        {art && <div className="absolute inset-0 rounded-full" style={{ background: completed ? `${worldColor}66` : 'rgba(255,255,255,0.25)' }} />}
        <span className="relative">
          {pro ? <Lock size={30} strokeWidth={2.8} className="text-white/90" />
            : lock === 'stars' ? <Star size={30} className="text-sunshine fill-sunshine" />
            : completed ? <Check size={40} strokeWidth={4} className="text-white drop-shadow" />
            : <span className="font-heading text-3xl font-bold text-navy drop-shadow-sm">{i + 1}</span>}
        </span>
        {completed && (
          <span className="absolute -top-1 -left-1 w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center" data-testid={`replay-icon-${game.id}`}>
            <RotateCw size={17} strokeWidth={3} style={{ color: worldColor }} />
          </span>
        )}
        {pro && <span className="absolute -top-2 -right-1 bg-sunshine text-navy font-heading font-bold text-[10px] px-2 py-0.5 rounded-full shadow">PRO</span>}
        {lock === 'stars' && (
          <span className="absolute -bottom-2 bg-white rounded-full px-2.5 py-0.5 shadow flex items-center gap-1" data-testid={`star-lock-${game.id}`}>
            <Star size={13} className="text-sunshine fill-sunshine" /><span className="font-heading font-bold text-xs text-navy">{need}</span>
          </span>
        )}
      </motion.button>
      {completed && (
        <div className="flex gap-0.5 mt-1.5" data-testid={`node-stars-${game.id}`}>
          {[1, 2, 3].map((s) => (
            <Star key={s} size={17} className={s <= (bestStars || 1) ? 'text-sunshine fill-sunshine drop-shadow' : 'text-white/40 fill-white/30'} />
          ))}
        </div>
      )}
      <span className={`mt-1 font-heading text-xs font-bold rounded-full px-3 py-1 shadow whitespace-nowrap max-w-[150px] truncate ${isCurrent ? 'bg-sunshine text-navy' : 'bg-white/90 text-navy'}`}>
        {isCurrent ? (en ? 'Play!' : 'Main!') : gameName(game, en)}
      </span>
    </div>
  );
}

export default function WorldPath() {
  const { worldId } = useParams();
  const nav = useNavigate();
  const { entitlement } = useEntitlement();
  const reduced = useReducedMotion();
  const st = getState();
  const en = st.profile.language === 'en';
  const stars = st.progress.stars;
  const gameStats = st.gameStats || {};

  const [overlay, setOverlay] = useState(null);
  const [marker, setMarker] = useState(null);
  const scrollRef = useRef(null);
  const sectionEls = useRef({});
  const rafRef = useRef(0);
  // Mirrors scrollTop, because React detaches refs BEFORE passive-effect
  // cleanup runs: reading scrollRef.current on unmount gives null, and the
  // save wrote 0 over the real position — the parent came back to the top of
  // an 8-world page every single time.
  const lastTopRef = useRef(0);

  const landing = PLAYABLE_REGIONS.find((r) => r.id === worldId);

  // Which world the header describes. Driven by scroll, not by the route — but
  // it starts on the world the route asked for so the first paint is correct.
  const [activeId, setActiveId] = useState(worldId);
  // Which sections are close enough to be worth mounting their images. Seeded
  // with the landing world and its neighbours so the screen lands on real
  // artwork rather than a placeholder.
  const [near, setNear] = useState(() => {
    const k = Math.max(0, regionIndex(worldId));
    return new Set(PLAYABLE_REGIONS.slice(Math.max(0, k - 1), k + 2).map((r) => r.id));
  });

  const cur = useMemo(() => currentJourney(entitlement, stars, gameStats), [entitlement, stars, gameStats]);

  const activeRegion = PLAYABLE_REGIONS.find((r) => r.id === activeId) || landing;
  const activeWorld = WORLDS[activeRegion?.id];

  // Stars earned in the world currently ON SCREEN, out of what it can give.
  // Each game awards up to 3 (2 rounds + a clean run), so the ceiling is n * 3.
  const worldStarMax = (activeRegion?.games.length || 0) * 3;
  const worldStars = useMemo(
    () => (activeRegion?.games || []).reduce((sum, gid) => sum + Math.min(3, gameStats[gid]?.bestStars || 0), 0),
    [activeRegion, gameStats],
  );

  // Land the screen before the first paint. Priority:
  //   1. the position we left from, but ONLY if the current node is still on
  //      screen there — otherwise the child comes back from the last game of a
  //      world and "Main!" has moved 800px down, out of sight.
  //   2. the current node, when it lives in the world that was asked for. This
  //      is the child's path and is exactly what this screen did before.
  //   3. the requested world's section, so /world/animals still lands on
  //      animals even when the child is playing somewhere else.
  // Instant, never smooth: smooth-scrolling up to 5,000px is a screen flying
  // past a 2-year-old, not a screen that "lands on their node".
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !landing) return;
    const vh = el.clientHeight || window.innerHeight;
    const secTop = (id) => sectionEls.current[id]?.offsetTop ?? 0;

    let curTop = null;
    if (cur && regionIndex(cur.regionId) >= 0) {
      const r = PLAYABLE_REGIONS[regionIndex(cur.regionId)];
      const i = r.games.indexOf(cur.gameId);
      if (i >= 0) curTop = secTop(r.id) + BANNER_H + i * ROW_H + 30;
    }

    let saved = null;
    try {
      const raw = sessionStorage.getItem(SCROLL_KEY);
      const v = raw ? JSON.parse(raw) : null;
      if (v && v.worldId === worldId && typeof v.top === 'number') saved = v.top;
    } catch { /* sessionStorage unavailable — fall through to the node */ }

    const nodeVisibleAt = (top) => curTop !== null && curTop >= top + 24 && curTop + ROW_H <= top + vh;

    let target;
    if (saved !== null && (curTop === null || nodeVisibleAt(saved))) target = saved;
    else if (curTop !== null && cur.regionId === worldId) target = curTop - LAND_OFFSET;
    else target = secTop(worldId);

    el.scrollTop = Math.max(0, target);
    lastTopRef.current = el.scrollTop;
  }, []); // eslint-disable-line

  // Music and the world greeting fire ONCE, for the world the screen lands on.
  //
  // Scrolling deliberately changes NEITHER. The eight worlds share only three
  // music tracks, so following the scroll would restart the same file over and
  // over, and a greeting per world across a 6,000px scroll is constant audio
  // churn — worse than silence. Scrolling past a world is browsing; ENTERING a
  // world is tapping a node, and the game screen sets its own audio there.
  // This is a decision, not an oversight. Please do not "fix" it.
  useEffect(() => {
    if (!landing) { nav('/home'); return; }
    audio.init();
    const w = WORLDS[worldId];
    audio.music(w.music);
    if (w.greet) audio.playVoice(w.greet);
    return () => {
      audio.stopVoice();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ worldId, top: lastTopRef.current })); } catch { /* ignore */ }
    };
  }, []); // eslint-disable-line

  // Two observers, no per-frame maths in a scroll handler.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;

    // A thin band across the middle of the viewport: the world that owns the
    // centre of the screen is the world the header describes.
    //
    // Two sections DO both cross the band at a boundary, so "whichever entry
    // came last" is not a rule — it picked the wrong world at the top of the
    // 3-game garden section. The rule is: the section covering MORE of the band
    // wins. Deterministic, and it matches what a parent would say they are
    // looking at.
    //
    // It only ever SETS an id, never clears one: at the very bottom the closing
    // card owns the band and nothing intersects, and the header must not blank
    // out there.
    const band = new Map();
    const activeObs = new IntersectionObserver((entries) => {
      for (const e of entries) band.set(e.target.dataset.worldId, e.isIntersecting ? e.intersectionRect.height : -1);
      let bestId = null;
      let best = 0;
      band.forEach((v, id) => { if (v > best) { best = v; bestId = id; } });
      if (bestId) setActiveId(bestId);
    }, { root, rootMargin: '-49% 0px -49% 0px', threshold: 0 });

    // What is close enough to deserve its background and its 3–4 thumbnails.
    const nearObs = new IntersectionObserver((entries) => {
      setNear((prev) => {
        let next = prev;
        for (const e of entries) {
          const id = e.target.dataset.worldId;
          if (e.isIntersecting === prev.has(id)) continue;
          if (next === prev) next = new Set(prev);
          if (e.isIntersecting) next.add(id); else next.delete(id);
        }
        return next;
      });
    }, { root, rootMargin: NEAR_MARGIN, threshold: 0 });

    Object.values(sectionEls.current).forEach((el) => {
      if (!el) return;
      activeObs.observe(el);
      nearObs.observe(el);
    });
    return () => { activeObs.disconnect(); nearObs.disconnect(); };
  }, []); // eslint-disable-line

  // Piko travels from the previous node to the current one (600ms ease-out).
  // He follows currentJourney(), not the route, so he is found anywhere in the
  // journey. Travel stays within one world; a cross-world step just appears.
  useEffect(() => {
    const k = cur ? regionIndex(cur.regionId) : -1;
    if (k < 0) return;
    const r = PLAYABLE_REGIONS[k];
    const w = Math.min(440, scrollRef.current?.clientWidth || window.innerWidth);
    const pos = (gid) => {
      const i = r.games.indexOf(gid);
      return i < 0 ? null : { x: (i % 2 === 0 ? 0.27 : 0.73) * w, y: i * ROW_H + 76, left: i % 2 === 0 };
    };
    const to = pos(cur.gameId);
    if (!to) return;
    let from = to;
    try {
      const prev = sessionStorage.getItem('pp_prev_node');
      if (prev && prev !== cur.gameId) { const p = pos(prev); if (p) from = p; }
      sessionStorage.setItem('pp_prev_node', cur.gameId);
    } catch { /* no sessionStorage — Piko simply does not travel */ }
    setMarker({ regionId: r.id, from, to });
  }, []); // eslint-disable-line

  if (!landing || !activeWorld) return null;

  const onScroll = () => {
    lastTopRef.current = scrollRef.current?.scrollTop ?? lastTopRef.current;
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ worldId, top: lastTopRef.current })); } catch { /* ignore */ }
    });
  };

  const tapGame = (g) => {
    const lock = gameLockState(entitlement, g.id, stars);
    if (lock === 'open') { audio.playSfx('pickup'); nav(`/game/${g.id}`); }
    else if (lock === 'stars') { audio.playSfx('hint'); setOverlay({ type: 'stars', need: starsNeeded(g.id) }); }
    else { audio.playVoice('hint'); setOverlay({ type: 'pro', game: g }); }
  };

  const nodePt = (i) => `${(i % 2 === 0 ? 27 : 73) * 4.4},${i * ROW_H + 76}`;

  return (
    <div className="fixed inset-0 overflow-hidden" data-testid={`world-path-${worldId}`} style={{ background: '#1D3557' }}>
      {/* header — follows the SCROLL, not the route. It must outrank the Piko
          marker: the marker sits 148px above its node, so on the first node of
          a world it rides up into this header and was covering the world
          objective. The objective is the one thing a parent reads here. */}
      {/* Near-opaque, not a soft scrim. Eight worlds now scroll UNDER this bar,
          so a translucent header let a world banner and a node label bleed
          through and sit on top of the objective and the witness line — the two
          sentences this screen exists to deliver. It fades only in its last few
          pixels so it does not read as a hard box across the artwork. */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center gap-3 px-4 pt-4 pb-3" style={{ background: 'linear-gradient(180deg, rgba(29,53,87,0.97) 0%, rgba(29,53,87,0.97) 74%, rgba(29,53,87,0.72) 90%, rgba(29,53,87,0) 100%)' }}>
        <button data-testid="world-back-btn" onClick={() => { audio.playSfx('tap'); nav('/home'); }}
          className="w-14 h-14 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center shrink-0">
          <ArrowLeft size={22} strokeWidth={3} className="text-navy" />
        </button>
        <div className="min-w-0">
          <h1 className="font-heading text-xl font-bold text-white drop-shadow-md truncate" data-testid="world-path-title">{t(activeWorld.nameKey)}</h1>
          <p className="font-body text-[11px] font-semibold text-white/80 drop-shadow truncate" data-testid="world-path-objective">{activeWorld.objective[en ? 'en' : 'ms']}</p>
          {/* What the parent will actually be able to see at home. This is the
              line that makes the objective testable instead of a claim. */}
          {activeWorld.witness && (
            <p className="font-body text-[11px] font-semibold text-sunshine/90 drop-shadow mt-0.5 leading-snug" data-testid="world-path-witness">
              {activeWorld.witness[en ? 'en' : 'ms']}
            </p>
          )}
        </div>
        {/* Stars earned in the world on screen, out of what it can give. The
            global wallet total sits on the child's home screen. */}
        <div className="ml-auto flex items-center gap-2 bg-white/90 backdrop-blur rounded-full px-4 py-2 shadow-lg min-h-[44px]" data-testid="world-star-count">
          <Star className="text-sunshine fill-sunshine" size={20} />
          <span className="font-heading font-bold text-navy">{worldStars}<span className="text-navy/45">/{worldStarMax}</span></span>
        </div>
      </div>

      {/* THE one scroll container — all eight worlds live in here. pan-y allows
          vertical scroll and nothing else: no swipe, no drag, no pinch, no
          horizontal pan reaches the child. */}
      <div ref={scrollRef} onScroll={onScroll}
        className="relative h-full overflow-y-auto overflow-x-hidden pb-10" data-testid="level-path"
        /* pan-y: vertical scroll and nothing else. No -webkit-overflow-scrolling
           either — that opts INTO inertial momentum, and momentum gestures are
           out of bounds in this product. overscroll contain stops the scroll
           chaining into a pull-to-refresh. */
        style={{ paddingTop: HEADER_PAD, touchAction: 'pan-y', overscrollBehavior: 'contain' }}>

        {PLAYABLE_REGIONS.map((r, k) => {
          const w = WORLDS[r.id];
          const colour = colourOf(r.id);
          const mounted = near.has(r.id);
          const pathH = pathHeight(r);
          const doneIdxs = r.games.map((g, i) => (gameStats[g]?.completed ? i : -1)).filter((i) => i >= 0);
          const curIdx = cur?.regionId === r.id ? r.games.indexOf(cur.gameId) : -1;
          const reached = Math.max(0, curIdx, ...(doneIdxs.length ? doneIdxs : [0]));
          const solidPts = r.games.slice(0, reached + 1).map((_, i) => nodePt(i)).join(' ');
          const dashedPts = r.games.map((_, i) => nodePt(i)).slice(reached).join(' ');
          const secStars = r.games.reduce((s, gid) => s + Math.min(3, gameStats[gid]?.bestStars || 0), 0);

          return (
            <section key={r.id} data-testid={`world-section-${r.id}`} data-world-id={r.id}
              ref={(el) => { sectionEls.current[r.id] = el; }}
              className="relative overflow-hidden"
              /* The height is FIXED from the game count, never from the
                 content, so a virtualised section is exactly as tall as a
                 mounted one and scrollHeight never moves. */
              style={{ height: sectionHeight(r), background: colour }}>

              {mounted && (
                <img src={w.bg} alt="" aria-hidden="true" loading="lazy" decoding="async"
                  className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.95)' }} />
              )}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(29,53,87,0.62) 0%, rgba(29,53,87,0.2) 24%, rgba(29,53,87,0.2) 86%, rgba(29,53,87,0.5) 100%)' }} />
              {/* the boundary, drawn without touching layout */}
              <div className="absolute top-0 inset-x-0 h-[5px]" style={{ background: colour, opacity: 0.95 }} />

              <div data-testid={`world-banner-${r.id}`} className="relative z-10 flex items-center gap-3 px-4" style={{ height: BANNER_H }}>
                <div className="w-1.5 self-stretch my-5 rounded-full shrink-0" style={{ background: colour }} />
                <div className="min-w-0">
                  <p className="font-body text-[10px] font-bold uppercase tracking-widest text-white/70 drop-shadow">
                    {t('worldNumber').replace('{n}', k + 1)}
                  </p>
                  <h2 className="font-heading text-lg font-bold text-white drop-shadow-md truncate">{t(w.nameKey)}</h2>
                  {!w.free && (
                    <p className="font-body text-[11px] font-semibold text-sunshine/90 drop-shadow flex items-center gap-1" data-testid={`world-banner-pro-${r.id}`}>
                      <Lock size={11} strokeWidth={3} className="shrink-0" />{t('worldProBanner')}
                    </p>
                  )}
                </div>
                <div className="ml-auto flex items-center gap-1.5 bg-white/85 rounded-full px-3 py-1.5 shadow shrink-0">
                  <Star size={15} className="text-sunshine fill-sunshine" />
                  <span className="font-heading font-bold text-sm text-navy">{secStars}<span className="text-navy/45">/{r.games.length * 3}</span></span>
                </div>
              </div>

              <div className="relative mx-auto w-full max-w-[440px]" style={{ height: pathH }}>
                {mounted && (
                  <>
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 440 ${pathH}`} preserveAspectRatio="none">
                      <polyline points={r.games.map((_, i) => nodePt(i)).join(' ')} fill="none" stroke="rgba(255,255,255,0.55)"
                        strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points={dashedPts} fill="none" stroke={colour} strokeOpacity="0.75"
                        strokeWidth="7" strokeDasharray="3 20" strokeLinecap="round" strokeLinejoin="round" data-testid="path-dashed" />
                      {reached > 0 && (
                        <polyline points={solidPts} fill="none" stroke={colour}
                          strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" data-testid="path-solid" />
                      )}
                    </svg>
                    {r.games.map((gid, i) => {
                      const g = getGame(gid);
                      const gs = gameStats[gid] || {};
                      return (
                        <PathNode key={gid} game={g} i={i} en={en} worldColor={colour}
                          lock={gameLockState(entitlement, gid, stars)}
                          need={starsNeeded(gid)}
                          completed={!!gs.completed} bestStars={gs.bestStars}
                          isCurrent={cur?.gameId === gid}
                          onTap={() => tapGame(g)} />
                      );
                    })}
                    {marker && marker.regionId === r.id && (
                      <motion.div className="absolute z-20 pointer-events-none" data-testid="piko-current-marker" style={{ left: 0, top: 0 }}
                        initial={reduced ? { x: marker.to.x, y: marker.to.y } : { x: marker.from.x, y: marker.from.y }}
                        animate={{ x: marker.to.x, y: marker.to.y }}
                        transition={{ duration: reduced ? 0 : 0.6, ease: 'easeOut' }}>
                        {/* Piko normally stands above the node. On the very
                            first node of a world that puts him inside the
                            banner (and, in world 1, the app header), washing
                            out the world objective — so there he steps to the
                            side instead. The gold ring and the "Main!" label
                            already mark where the child is. */}
                        <div style={marker.to.y >= 152
                          ? { marginLeft: -32, marginTop: -148 }
                          : { marginLeft: marker.to.left ? 60 : -124, marginTop: -30 }}>
                          <motion.div animate={reduced ? {} : { y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 1.4 }}>
                            <Piko pose="happy" size={64} float={false} />
                          </motion.div>
                        </div>
                      </motion.div>
                    )}
                  </>
                )}
              </div>
            </section>
          );
        })}

        {/* The journey ends here, and says so — otherwise a parent who has
            scrolled 6,000px cannot tell whether there is more below. */}
        <div data-testid="journey-end" className="relative flex flex-col items-center gap-2 py-9 px-6 text-center">
          <Piko pose="happy" size={56} float={false} />
          <p className="font-heading text-sm font-bold text-white/90 drop-shadow">{t('journeyEnd')}</p>
        </div>
      </div>

      <AnimatePresence>
        {overlay && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-navy/50 backdrop-blur-md flex items-center justify-center px-6" data-testid="locked-overlay">
            <motion.div initial={{ scale: 0.9, y: 16 }} animate={{ scale: 1, y: 0 }}
              className="bg-white/95 rounded-[2.5rem] p-7 max-w-sm w-full text-center shadow-2xl">
              <Piko pose="think" size={104} float={false} />
              {overlay.type === 'stars' ? (
                <>
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <Star size={30} className="text-sunshine fill-sunshine" />
                    <span className="font-heading text-3xl font-bold text-navy">{overlay.need}</span>
                  </div>
                  <p className="font-heading text-lg font-bold text-navy mt-2" data-testid="stars-locked-message">{en ? 'Collect more stars to open this game!' : 'Kumpul bintang lagi untuk buka permainan ini!'}</p>
                </>
              ) : (
                <p className="font-heading text-xl font-bold text-navy mt-3" data-testid="locked-message">{t('lockedMsg')}</p>
              )}
              <div className="flex flex-col gap-3 mt-5">
                {overlay.type === 'pro' && overlay.game && !hasTriedGame(overlay.game.id) && (
                  <button data-testid="try-game-btn"
                    onClick={() => { audio.playSfx('pickup'); nav(`/game/${overlay.game.id}?trial=1`); }}
                    className="min-h-[64px] rounded-2xl bg-leaf font-heading font-bold text-lg text-navy"
                    style={{ boxShadow: 'inset 0 -6px 0 rgba(0,0,0,0.12)' }}>{t('tryFirst')}</button>
                )}
                <button data-testid="locked-back-btn" onClick={() => setOverlay(null)}
                  className="min-h-[64px] rounded-2xl bg-sunshine font-heading font-bold text-lg text-navy"
                  style={{ boxShadow: 'inset 0 -6px 0 rgba(0,0,0,0.12)' }}>{en ? 'Back to my games' : 'Kembali ke permainan saya'}</button>
                {overlay.type === 'pro' && (
                  <button data-testid="locked-parent-btn" onClick={() => nav('/parent-gate?next=upgrade')}
                    className="min-h-[56px] rounded-2xl bg-cream font-body font-bold text-navy/70">{t('parentArea')}</button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
